import { apiV2, errorHandler } from "./index";
import { markGeneralDataWrite, getGeneralDataWriteSeq } from "./generalDataSync";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  API_ERROR,
  FETCH_ALL_QUICK_TAPS,
  FETCH_ALL_GENERAL_DATA,
  CLEAR_HIGHLIGHT,
  FETCH_ALL_QUICK_TAPS_START,
  FETCH_QUICK_TAPS_PAGE_START,
  FETCH_QUICK_TAPS_PAGE_SUCCESS,
  FETCH_QUICK_TAPS_PAGE_FAILURE,
} from "../constants/ActionTypes";

export function fetchAllQuickTapSessions() {
  return function (dispatch) {
    dispatch({
      type: FETCH_ALL_QUICK_TAPS_START
    });

    apiV2.get('/quick-tap-sessions')
      .then((response) => {
        dispatch({
          type: FETCH_ALL_QUICK_TAPS,
          payload: response.data.objects
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

/**
 * Paginated quick taps list — calls GET /quick-tap-sessions with the
 * new paginated-mode query params. Returns the response envelope:
 *
 *   { data: [QuickTapSessionPaginatedItem…], page, per_page,
 *     page_count, has_more, total_count }
 *
 * Spec: cms-api-openapi.yaml `/quick-tap-sessions` (paginated mode
 * triggers if any of page/per_page/sort/dir/q/active/featured/free/
 * parent_type/author_id are present). We always pass `page` + `per_page`
 * so the response is guaranteed to be the paginated envelope.
 *
 * Mirrors `fetchSessionsPage`. Empty / undefined values are dropped so
 * we don't send `?q=` etc.
 */
export function fetchQuickTapsPage(params = {}) {
  return function (dispatch) {
    dispatch({ type: FETCH_QUICK_TAPS_PAGE_START, payload: params });

    const query = {
      page: params.page || 1,
      per_page: params.per_page || 50,
    };
    [
      'sort', 'dir', 'q', 'active', 'featured',
      'free', 'parent_type', 'author_id',
    ].forEach((k) => {
      const v = params[k];
      if (v === null || v === undefined || v === '') return;
      query[k] = v;
    });

    return apiV2
      .get('/quick-tap-sessions', { params: query })
      .then((response) => {
        dispatch({
          type: FETCH_QUICK_TAPS_PAGE_SUCCESS,
          payload: response.data,
        });
        return response.data;
      })
      .catch((error) => {
        dispatch({
          type: FETCH_QUICK_TAPS_PAGE_FAILURE,
          payload: error.response?.data?.message || 'Failed to load quick taps',
        });
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function fetchOneQuickTapSession(quick_tap_id) {
  return function (dispatch) {
    return apiV2.get(`/quick-tap-sessions/${quick_tap_id}`)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function addEditQuickTapSession(options, onSuccess) {
  return function (dispatch) {
    const sessionData = typeof options.quick_tap_session_json === 'string'
      ? JSON.parse(options.quick_tap_session_json)
      : options.quick_tap_session_json;

    const timestamps = options.timestamps_json
      ? (typeof options.timestamps_json === 'string'
        ? JSON.parse(options.timestamps_json)
        : options.timestamps_json)
      : undefined;

    const isUpdate = !!sessionData.quick_tap_id;

    const payload = {
      quick_tap_title: sessionData.quick_tap_title,
      quick_tap_description: sessionData.quick_tap_description,
      quick_tap_is_new: sessionData.quick_tap_is_new,
      quick_tap_is_active: sessionData.quick_tap_is_active,
      quick_tap_is_featured: sessionData.quick_tap_is_featured,
      quick_tap_is_premium: sessionData.quick_tap_is_premium,
      quick_tap_is_dark_mode: sessionData.quick_tap_is_dark_mode,
      quick_tap_image: sessionData.quick_tap_image,
      quick_tap_video_playback_id: sessionData.quick_tap_video_playback_id,
      quick_tap_author_id: sessionData.quick_tap_author_id,
      quick_tap_duration: sessionData.quick_tap_duration,
      quick_tap_duration_in_sec: sessionData.quick_tap_duration_in_sec,
      quick_tap_bg_music_url: sessionData.quick_tap_bg_music_url,
      quick_tap_deep_link_ios: sessionData.quick_tap_deep_link_ios,
      quick_tap_deep_link_android: sessionData.quick_tap_deep_link_android,
      quick_tap_deep_link_image: sessionData.quick_tap_deep_link_image,
      category_id: options.category_id,
      subcategory_id: options.subcategory_id,
    };

    if (timestamps) {
      payload.timestamps = timestamps;
    }

    const request = isUpdate
      ? apiV2.put(`/quick-tap-sessions/${sessionData.quick_tap_id}`, payload)
      : apiV2.post('/quick-tap-sessions', payload);

    return request
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function duplicateQuickTapSession(quickTapId) {
  return function (dispatch) {
    return apiV2
      .post(`/quick-tap-sessions/${quickTapId}/duplicate`)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function deleteQuickTapSession(quick_tap_id, onSuccess) {
  return function (dispatch) {
    apiV2.delete(`/quick-tap-sessions/${quick_tap_id}`)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Session Deleted!",
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function clearQuickTapSessions() {
  return function (dispatch) {
    dispatch({
      type: FETCH_ALL_QUICK_TAPS,
      payload: null,
    });
  };
}

export function fetchQuickTapsIntro() {
  return function (dispatch) {
    const seq = getGeneralDataWriteSeq();
    apiV2.get('/quick-tap-sessions/intro')
      .then((response) => {
        // A save landed while this fetch was in flight — its payload is
        // stale, don't revert the just-written baseline.
        if (seq !== getGeneralDataWriteSeq()) return;
        // sendResponse wraps as { objects: [{ quick_tap_intro: "..." }] }
        const data = response.data.objects?.[0] || response.data;
        dispatch({
          type: FETCH_ALL_GENERAL_DATA,
          payload: data,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function fetchQuickTapsFeaturedIntro() {
  return function (dispatch) {
    const seq = getGeneralDataWriteSeq();
    apiV2.get('/quick-tap-sessions/featured-intro')
      .then((response) => {
        // A save landed while this fetch was in flight — its payload is
        // stale, don't revert the just-written baseline.
        if (seq !== getGeneralDataWriteSeq()) return;
        dispatch({
          type: FETCH_ALL_GENERAL_DATA,
          payload: response.data,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function updateQuickTapsIntro(quickTapsIntro) {
  return function (dispatch, getState) {
    return apiV2.put('/general-info/quick_tap_intro', {
      general_info_value: quickTapsIntro,
    })
      .then(() => {
        // Sync the saved value into the general_data slice so consumers
        // using it as their baseline (InAppDescriptionPanel) see the save
        // reflected without a refetch.
        markGeneralDataWrite();
        dispatch({
          type: FETCH_ALL_GENERAL_DATA,
          payload: {
            ...getState().general_data?.general_data,
            quick_tap_intro: quickTapsIntro,
          },
        });
        Swal.fire({
          icon: "success",
          title: "Saved!",
          timer: 1000,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function updateQuickTapsFeaturedIntro(quickTapsIntro) {
  return function (dispatch, getState) {
    return apiV2.put('/general-info/quick_taps_featured_intro', {
      general_info_value: quickTapsIntro,
    })
      .then(() => {
        // Sync the saved value into the general_data slice so consumers
        // using it as their baseline (InAppDescriptionPanel) see the save
        // reflected without a refetch.
        markGeneralDataWrite();
        dispatch({
          type: FETCH_ALL_GENERAL_DATA,
          payload: {
            ...getState().general_data?.general_data,
            quick_taps_featured_intro: quickTapsIntro,
          },
        });
        Swal.fire({
          icon: "success",
          title: "Saved!",
          timer: 1000,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function fetchAllQuickTapFeaturedSessions() {
  return function (dispatch) {
    apiV2.get('/quick-tap-sessions', { params: { featured: 1 } })
      .then((response) => {
        dispatch({
          type: FETCH_ALL_QUICK_TAPS,
          payload: response.data.objects,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function updateQuickTapFeaturedOrder(featured_ids) {
  return function (dispatch) {
    const ids = typeof featured_ids === 'string' ? JSON.parse(featured_ids) : featured_ids;
    apiV2.put('/quick-tap-sessions/featured-sort', {
      featured_ids: ids
    })
      .then(() => {
        dispatch({
          type: CLEAR_HIGHLIGHT,
          payload: null
        });
      })
      .catch((error) => {
        dispatch({
          type: CLEAR_HIGHLIGHT,
          payload: null
        });
        fetchAllQuickTapFeaturedSessions()(dispatch);
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}
